/**
 * OverlayPage — Compact always-on-top race strategy overlay
 *
 * Reads the selected strategy plan from localStorage (set by the main app)
 * and recalculates targets based on real telemetry data every second.
 *
 * Shows the driver exactly what to do RIGHT NOW:
 *   - Action banner: KEEP PACE / SAVE FUEL / PUSH / PIT NOW
 *   - Fuel target per lap (what you should burn)
 *   - Lap time target (what you should hit)
 *   - Current vs target deltas
 *   - Laps until next pit, fuel to add
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { RacePlan, PlanStint, StaticStrategyInput } from "../utils/staticStrategyPlanner";
import { StaticStrategyPlanner } from "../utils/staticStrategyPlanner";

// ── Types ────────────────────────────────────────────────────────────────

interface SessionStatus {
  simConnected: boolean;
  sessionType: string | null;
  trackName: string | null;
}

interface SessionData {
  raceDurationMin?: number;
  fuelCapacity?: number;
  sessionTimeRemain?: number;
  yourCarClassShortName?: string;
}

interface LapData {
  lapNumber: number;
  lapTime: number | null;
  fuelLevel: number | null;
}

type Action = 'keep_pace' | 'save_fuel' | 'push' | 'pit_now' | 'no_plan' | 'waiting';

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtTime(s: number | null): string {
  if (!s || s <= 0) return "--:--.---";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toFixed(3).padStart(6, "0")}`;
}

function fmtDelta(delta: number, unit: string): string {
  const prefix = delta > 0 ? "+" : "";
  return `${prefix}${delta.toFixed(2)}${unit}`;
}

// ── Component ────────────────────────────────────────────────────────────

export const OverlayPage: React.FC = () => {
  const [status, setStatus] = useState<SessionStatus>({
    simConnected: false, sessionType: null, trackName: null,
  });
  const [session, setSession] = useState<SessionData>({});
  const [laps, setLaps] = useState<LapData[]>([]);
  const lapsRef = useRef(laps);
  const sessionRef = useRef(session);
  lapsRef.current = laps;
  sessionRef.current = session;

  // Plan from API (originally selected by user)
  const [plan, setPlan] = useState<RacePlan | null>(null);
  const [planInput, setPlanInput] = useState<StaticStrategyInput | null>(null);

  // Active plan — starts as the selected plan, gets re-calculated after pits
  const [activePlan, setActivePlan] = useState<RacePlan | null>(null);
  const [planUpdatedAt, setPlanUpdatedAt] = useState<number | null>(null);

  // Pit tracking: how many pits have happened since the last re-plan
  const prevPitCountRef = useRef(0);
  const pitsAtLastReplanRef = useRef(0);

  // Adaptive recalculation: track what pace/fuel the current plan is based on
  const planBasisRef = useRef<{ pace: number; fuel: number } | null>(null);
  // Thresholds for triggering recalculation
  const PACE_THRESHOLD = 0.3;   // seconds — recalc if rolling avg shifts by ±0.3s
  const FUEL_THRESHOLD = 0.1;   // liters — recalc if rolling avg shifts by ±0.1 L/lap

  // ── Load plan from API ─────────────────────────────────────────────────

  useEffect(() => {
    async function loadPlan() {
      try {
        const base = window.location.origin;
        const res = await fetch(`${base}/api/plan?` + Date.now());
        if (!res.ok) return;
        const data = await res.json();
        setPlan(data.plan ?? null);
        setPlanInput(data.input ?? null);
      } catch {
        // API not available yet
      }
    }
    loadPlan();
    const id = setInterval(loadPlan, 5000);
    return () => clearInterval(id);
  }, []);

  // When user selects a new plan, reset everything
  useEffect(() => {
    setActivePlan(plan);
    setPlanUpdatedAt(null);
    prevPitCountRef.current = 0;
    pitsAtLastReplanRef.current = 0;
    planBasisRef.current = null; // reset adaptive basis so it re-initialises from live data
  }, [plan]);

  // Dismiss "PLAN UPDATED" flash after 6s
  useEffect(() => {
    if (!planUpdatedAt) return;
    const id = setTimeout(() => setPlanUpdatedAt(null), 6000);
    return () => clearTimeout(id);
  }, [planUpdatedAt]);

  // ── Rolling averages (last 5 clean laps, outlier-filtered) ───────────
  // Lap 1 is always the formation/out-lap: skip it for pace & fuel averages
  // (same rule as the Telemetry Analyzer's ANALYZER_WARMUP_LAPS = 2).
  // It still counts as a driven lap for laps.length / stint logic.
  const OVERLAY_WARMUP_LAPS = 1; // skip first N lap numbers

  const recentAvg = useMemo(() => {
    // Exclude warmup / formation laps from pace calculation
    const valid = laps.filter(
      l => l.lapTime !== null && l.lapTime > 0 && l.lapNumber > OVERLAY_WARMUP_LAPS
    );
    if (valid.length === 0) return { lapTime: null, fuelPerLap: null };

    // Outlier filtering: remove laps > mean + 2σ (traffic, mistakes, slow zones)
    const times = valid.map(l => l.lapTime!);
    const meanLT = times.reduce((s, t) => s + t, 0) / times.length;
    const stdLT = Math.sqrt(times.reduce((s, t) => s + (t - meanLT) ** 2, 0) / times.length);
    const cutoff = meanLT + 2 * stdLT;
    const cleanValid = valid.filter(l => l.lapTime! <= cutoff);
    const pool = cleanValid.length >= 3 ? cleanValid : valid;

    const recent = pool.slice(-5);
    const avgTime = recent.reduce((s, l) => s + (l.lapTime ?? 0), 0) / recent.length;

    // Fuel consumption: only from laps where fuel decreased (skip pit laps & warmup)
    const consumptions: number[] = [];
    for (let i = 1; i < laps.length; i++) {
      // Skip the transition INTO lap OVERLAY_WARMUP_LAPS+1 (formation lap fuel delta)
      if (laps[i].lapNumber <= OVERLAY_WARMUP_LAPS + 1) continue;
      const prev = laps[i - 1].fuelLevel;
      const cur = laps[i].fuelLevel;
      if (prev !== null && cur !== null && prev > cur) {
        consumptions.push(prev - cur);
      }
    }

    // Outlier filtering on fuel consumption (remove abnormally high usage)
    let avgFuel: number | null = null;
    if (consumptions.length >= 3) {
      const meanFC = consumptions.reduce((s, c) => s + c, 0) / consumptions.length;
      const stdFC = Math.sqrt(consumptions.reduce((s, c) => s + (c - meanFC) ** 2, 0) / consumptions.length);
      const fuelCutoff = meanFC + 2 * stdFC;
      const cleanCons = consumptions.filter(c => c <= fuelCutoff);
      const fuelPool = cleanCons.length >= 3 ? cleanCons : consumptions;
      const recentCons = fuelPool.slice(-5);
      avgFuel = recentCons.reduce((s, c) => s + c, 0) / recentCons.length;
    } else if (consumptions.length > 0) {
      const recentCons = consumptions.slice(-5);
      avgFuel = recentCons.reduce((s, c) => s + c, 0) / recentCons.length;
    }

    return { lapTime: avgTime, fuelPerLap: avgFuel };
  }, [laps]);

  const recentAvgRef = useRef(recentAvg);
  recentAvgRef.current = recentAvg;

  const currentFuel = laps.length > 0 ? laps[laps.length - 1].fuelLevel : null;

  // Last lap fuel consumption (only valid if no pit on last lap)
  const lastLapFuel = useMemo(() => {
    if (laps.length < 2) return null;
    const prev = laps[laps.length - 2].fuelLevel;
    const cur = laps[laps.length - 1].fuelLevel;
    if (prev === null || cur === null || cur >= prev) return null; // pit lap
    return prev - cur;
  }, [laps]);

  // ── Re-plan on any pit stop ────────────────────────────────────────────
  // Triggered whenever a new pit is detected in the laps data.
  // Uses actual rolling averages + iRacing remaining time as the new inputs.
  // This handles both planned pits (confirms optimal strategy) and
  // unplanned pits (damage, rain, safety car) by re-optimising from reality.

  useEffect(() => {
    if (!activePlan || !planInput) return;

    // Count pits in current laps data
    let pitCount = 0;
    for (let i = 1; i < laps.length; i++) {
      const prev = laps[i - 1].fuelLevel;
      const cur = laps[i].fuelLevel;
      if (prev !== null && cur !== null && cur > prev + 1) pitCount++;
    }

    if (pitCount <= prevPitCountRef.current) return; // no new pit
    prevPitCountRef.current = pitCount;

    const avg = recentAvgRef.current;
    if (!avg.lapTime || !avg.fuelPerLap) return;

    // Use iRacing remaining time; fall back to estimate
    const remainSec = sessionRef.current.sessionTimeRemain;
    if (!remainSec || remainSec <= 60) return; // too close to end to replan
    const remainMin = remainSec / 60;

    // Defer the heavy planRace() call so it runs after the current frame paints.
    // Without this, the S/F crossing render + planRace() block the same JS frame
    // causing a visible stutter in the overlay.
    const pitCountAtTrigger = pitCount;
    const snapLapTime = avg.lapTime!;
    const snapFuelPerLap = avg.fuelPerLap!;
    const timerId = setTimeout(() => {
      try {
        const planner = new StaticStrategyPlanner();
        const newInput: StaticStrategyInput = {
          ...planInput,
          avgFuelPerLap: snapFuelPerLap,
          avgLapTime: snapLapTime,
          raceDurationMinutes: remainMin,
        };
        const result = planner.planRace(newInput);
        if (result.bestPlan.totalLaps > 0) {
          setActivePlan(result.bestPlan);
          pitsAtLastReplanRef.current = pitCountAtTrigger;
          setPlanUpdatedAt(Date.now());
          planBasisRef.current = { pace: snapLapTime, fuel: snapFuelPerLap };
        }
      } catch { /* keep current plan */ }
    }, 0);
    return () => clearTimeout(timerId);
  }, [laps, activePlan, planInput]);

  // ── Adaptive recalculation: recalc when pace/fuel drift or plan is infeasible ──
  // Fires every lap. Checks:
  //   1. Rolling avg lap time shifted by > PACE_THRESHOLD from plan basis
  //   2. Rolling avg fuel/lap shifted by > FUEL_THRESHOLD from plan basis
  //   3. Plan is infeasible (can't make it to planned pit with remaining fuel)
  // On trigger → recalculate strategy with current real pace + remaining time.

  useEffect(() => {
    if (!activePlan || !planInput) return;

    const avg = recentAvgRef.current;
    if (!avg.lapTime || !avg.fuelPerLap) return;

    // Use iRacing remaining time; fall back to estimate
    const remainSec = sessionRef.current.sessionTimeRemain;
    if (!remainSec || remainSec <= 60) return;

    // Initialise basis on first valid data
    if (!planBasisRef.current) {
      planBasisRef.current = { pace: avg.lapTime, fuel: avg.fuelPerLap };
      return;
    }

    const basis = planBasisRef.current;
    const paceDrift = Math.abs(avg.lapTime - basis.pace);
    const fuelDrift = Math.abs(avg.fuelPerLap - basis.fuel);

    // Feasibility check: can we reach the planned pit lap with current fuel?
    let infeasible = false;
    if (laps.length > 0) {
      const fuel = laps[laps.length - 1].fuelLevel;
      if (fuel !== null && avg.fuelPerLap > 0) {
        // Count current stint laps + laps until planned pit
        let pitCount = 0;
        let stintStartLap = 0;
        for (let i = 1; i < laps.length; i++) {
          const prev = laps[i - 1].fuelLevel;
          const cur = laps[i].fuelLevel;
          if (prev !== null && cur !== null && cur > prev + 1) {
            pitCount++;
            stintStartLap = i;
          }
        }
        const lapsInStint = laps.length - stintStartLap;
        const stintIdx = pitCount - pitsAtLastReplanRef.current;
        const plannedStint = activePlan.stints[stintIdx];
        if (plannedStint && plannedStint.pitAfter) {
          const lapsToPlannedPit = Math.max(0, plannedStint.laps - lapsInStint);
          const fuelNeeded = lapsToPlannedPit * avg.fuelPerLap + 0.5; // 0.5L safety
          if (fuel < fuelNeeded && lapsToPlannedPit > 0) {
            infeasible = true;
          }
        }
      }
    }

    // Check if any trigger fires
    const paceTriggered = paceDrift >= PACE_THRESHOLD;
    const fuelTriggered = fuelDrift >= FUEL_THRESHOLD;

    if (!paceTriggered && !fuelTriggered && !infeasible) return;

    // Defer the heavy planRace() call so it runs after the current frame paints.
    const snapAvg = avg;
    const remainMin = remainSec / 60;
    const timerId = setTimeout(() => {
      try {
        const planner = new StaticStrategyPlanner();
        const newInput: StaticStrategyInput = {
          ...planInput,
          avgFuelPerLap: snapAvg.fuelPerLap!,
          avgLapTime: snapAvg.lapTime!,
          raceDurationMinutes: remainMin,
        };
        const result = planner.planRace(newInput);
        if (result.bestPlan.totalLaps > 0) {
          setActivePlan(result.bestPlan);
          setPlanUpdatedAt(Date.now());
          planBasisRef.current = { pace: snapAvg.lapTime!, fuel: snapAvg.fuelPerLap! };
        }
      } catch { /* keep current plan */ }
    }, 0);
    return () => clearTimeout(timerId);
  }, [laps, activePlan, planInput]);

  // ── Stint info (relative to current active plan) ───────────────────────

  const stintInfo = useMemo(() => {
    if (!activePlan || laps.length === 0) return null;

    let pitCount = 0;
    let stintStartLap = 0;
    for (let i = 1; i < laps.length; i++) {
      const prev = laps[i - 1].fuelLevel;
      const cur = laps[i].fuelLevel;
      if (prev !== null && cur !== null && cur > prev + 1) {
        pitCount++;
        stintStartLap = i;
      }
    }

    // How deep into activePlan's stints are we?
    // activePlan was (re)generated at pitsAtLastReplanRef pits, so index 0 = current stint
    const stintIndexInPlan = pitCount - pitsAtLastReplanRef.current;
    const lapsInCurrentStint = laps.length - stintStartLap;
    const plannedStint: PlanStint | undefined = activePlan.stints[stintIndexInPlan];
    const nextPlannedStint: PlanStint | undefined = activePlan.stints[stintIndexInPlan + 1];
    const pitsRemaining = Math.max(0, activePlan.pitStops - stintIndexInPlan);

    return { lapsInCurrentStint, stintIndexInPlan, plannedStint, nextPlannedStint, pitCount, pitsRemaining };
  }, [activePlan, laps]);

  // ── Overlay state ──────────────────────────────────────────────────────
  // Targets come from the active plan, which is auto-recalculated when
  // real pace or fuel consumption drifts beyond thresholds, or when the
  // current plan becomes infeasible (can't reach planned pit with remaining fuel).

  const overlayState = useMemo(() => {
    const fuel = currentFuel ?? 0;

    if (!activePlan || !planInput) {
      return {
        action: 'no_plan' as Action,
        targetFuelPerLap: 0, targetLapTime: 0,
        actualFuelPerLap: 0, actualLapTime: 0,
        fuelDelta: 0, paceDelta: 0,
        lapsInStint: 0, lapsUntilPit: 0,
        fuelRemaining: fuel, fuelLapsLeft: 0,
        nextPitType: null as 'full' | 'splash' | null,
        nextPitFuelAdd: 0,
        lapsRemaining: 0, pitsRemaining: 0,
        planName: '', message: 'No plan selected',
      };
    }

    if (!recentAvg.lapTime || !recentAvg.fuelPerLap || laps.length < 2) {
      return {
        action: 'waiting' as Action,
        targetFuelPerLap: activePlan.adjustedFuelPerLap,
        targetLapTime: activePlan.adjustedLapTime,
        actualFuelPerLap: 0, actualLapTime: 0,
        fuelDelta: 0, paceDelta: 0,
        lapsInStint: 0, lapsUntilPit: 0,
        fuelRemaining: fuel, fuelLapsLeft: 0,
        nextPitType: null as 'full' | 'splash' | null,
        nextPitFuelAdd: 0,
        lapsRemaining: 0, pitsRemaining: 0,
        planName: activePlan.name, message: 'Waiting for lap data...',
      };
    }

    // Fixed targets from the plan — never recalculated mid-stint
    const targetFuelPerLap = activePlan.adjustedFuelPerLap;
    const targetLapTime = activePlan.adjustedLapTime;
    const actualFuelPerLap = recentAvg.fuelPerLap;
    const actualLapTime = recentAvg.lapTime;

    const fuelDelta = actualFuelPerLap - targetFuelPerLap;   // +ve = using more than plan
    const paceDelta = actualLapTime - targetLapTime;          // +ve = slower than plan

    // Use iRacing remaining time; fall back to estimate
    const remainSec = session.sessionTimeRemain ?? Math.max(0,
      planInput.raceDurationMinutes * 60 - laps.length * actualLapTime);
    const lapsRemaining = actualLapTime > 0 ? Math.floor(remainSec / actualLapTime) : 0;

    const si = stintInfo;
    const lapsInStint = si?.lapsInCurrentStint ?? laps.length;
    const plannedStintLaps = si?.plannedStint?.laps ?? Math.ceil((fuel - 0.5) / targetFuelPerLap);
    const lapsUntilPit = Math.max(0, plannedStintLaps - lapsInStint);
    const fuelLapsLeft = actualFuelPerLap > 0 ? Math.floor((fuel - 0.5) / actualFuelPerLap) : 0;
    const pitsRemaining = si?.pitsRemaining ?? activePlan.pitStops;

    const nextPitType = si?.plannedStint?.pitType ?? null;

    // Fuel to add at the NEXT pit stop — calculated live from iRacing remaining time,
    // not from the plan's stale fuelAtEnd.
    // Approach: how many laps will remain AFTER the next pit?
    //   lapsAfterPit = lapsRemaining - lapsUntilPit
    // How much fuel do those laps need?
    //   fuelNeeded = lapsAfterPit × actualFuelPerLap + 0.5L safety
    // How much fuel will be in the tank at pit entry (after running lapsUntilPit more laps)?
    //   fuelAtPit = max(0, fuel - lapsUntilPit × actualFuelPerLap)
    // Fuel to add = clamp(fuelNeeded - fuelAtPit, 0, fuelCapacity)
    // This is always accurate regardless of pace changes since race start.
    let nextPitFuelAdd = 0;
    if (si?.nextPlannedStint && actualFuelPerLap > 0 && lapsUntilPit > 0) {
      const lapsAfterPit = Math.max(0, lapsRemaining - lapsUntilPit);
      const fuelNeededAfterPit = lapsAfterPit * actualFuelPerLap + 0.5;
      const fuelAtPitEntry = Math.max(0, fuel - lapsUntilPit * actualFuelPerLap);
      nextPitFuelAdd = Math.min(
        planInput.fuelCapacity,
        Math.max(0, fuelNeededAfterPit - fuelAtPitEntry)
      );
    }

    // ── Action ─────────────────────────────────────────────────────────
    let action: Action;
    let message: string;

    if (fuelLapsLeft <= 1 && pitsRemaining > 0) {
      action = 'pit_now';
      message = 'OUT OF FUEL — PIT THIS LAP';
    } else if (fuelLapsLeft < lapsUntilPit && lapsUntilPit <= 3 && pitsRemaining > 0) {
      action = 'pit_now';
      message = `${fuelLapsLeft} fuel laps, pit in ${lapsUntilPit}`;
    } else if (fuelDelta > 0.12) {
      // Burning more than plan — need to save
      action = 'save_fuel';
      message = `+${fuelDelta.toFixed(2)}L over plan target (${targetFuelPerLap.toFixed(2)}L/lap)`;
    } else if (fuelDelta < -0.20 && lapsRemaining > 5 && pitsRemaining === 0) {
      // Saving significantly more than needed with no stops left
      action = 'push';
      message = `${(-fuelDelta).toFixed(2)}L under plan — can push to finish`;
    } else if (fuelLapsLeft < lapsUntilPit && lapsUntilPit > 3) {
      // Will run out before planned pit without saving
      action = 'save_fuel';
      message = `Only ${fuelLapsLeft} fuel laps, pit in ${lapsUntilPit} — save fuel`;
    } else {
      action = 'keep_pace';
      message = `On plan — ${targetLapTime > 0 ? fmtTime(targetLapTime) : '--'} @ ${targetFuelPerLap.toFixed(2)}L/lap`;
    }

    return {
      action, targetFuelPerLap, targetLapTime,
      actualFuelPerLap, actualLapTime,
      fuelDelta, paceDelta,
      lapsInStint, lapsUntilPit,
      fuelRemaining: fuel, fuelLapsLeft,
      nextPitType, nextPitFuelAdd,
      lapsRemaining, pitsRemaining,
      planName: activePlan.name, message,
    };
  }, [activePlan, planInput, recentAvg, currentFuel, laps, stintInfo, session.sessionTimeRemain]);

  // ── Polling telemetry ──────────────────────────────────────────────────

  const poll = useCallback(async () => {
    try {
      const res = await fetch("/api/telemetry?" + Date.now());
      if (!res.ok) return;
      const data = await res.json();
      if (data.status) setStatus(data.status);
      if (data.session) {
        // Only update session state when a value actually changed
        setSession(s => {
          const keys = Object.keys(data.session) as (keyof SessionData)[];
          const changed = keys.some(k => data.session[k] !== s[k]);
          return changed ? { ...s, ...data.session } : s;
        });
      }
      if (data.laps && data.laps.length > lapsRef.current.length) {
        setLaps(data.laps);
      }
    } catch {
      setStatus(s => ({ ...s, simConnected: false }));
    }
  }, []);

  useEffect(() => {
    const id = setInterval(poll, 1000);
    poll();
    return () => clearInterval(id);
  }, [poll]);

  // ── Action banner config ───────────────────────────────────────────────

  const actionConfig: Record<Action, { bg: string; border: string; text: string; label: string; icon: string }> = {
    keep_pace:  { bg: 'bg-green-900/60',  border: 'border-green-500', text: 'text-green-400',  label: 'KEEP PACE',  icon: '✓' },
    save_fuel:  { bg: 'bg-yellow-900/60',  border: 'border-yellow-500', text: 'text-yellow-400', label: 'SAVE FUEL',  icon: '⇣' },
    push:       { bg: 'bg-blue-900/60',    border: 'border-blue-500',  text: 'text-blue-400',   label: 'PUSH',       icon: '⇡' },
    pit_now:    { bg: 'bg-red-900/70',     border: 'border-red-500',   text: 'text-red-400',    label: 'PIT NOW',    icon: '⚠' },
    no_plan:    { bg: 'bg-slate-800/60',   border: 'border-slate-600', text: 'text-slate-400',  label: 'NO PLAN',    icon: '—' },
    waiting:    { bg: 'bg-slate-800/60',   border: 'border-slate-600', text: 'text-slate-400',  label: 'WAITING',    icon: '…' },
  };

  const ac = actionConfig[overlayState.action];
  const connected = status.simConnected;
  const hasPlan = activePlan !== null;
  const hasData = laps.length >= 2 && recentAvg.lapTime !== null;
  const planUpdatedFlash = planUpdatedAt !== null;

  // Format remaining race time from iRacing
  const remainSec = session.sessionTimeRemain;
  const remainStr = (remainSec && remainSec > 0)
    ? `${Math.floor(remainSec / 3600)}h${Math.floor((remainSec % 3600) / 60)}m`
    : null;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen bg-black/85 text-white font-mono text-xs select-none"
      style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1 bg-slate-900/90 border-b border-slate-700/50">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">SRE Overlay</span>
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`} />
          <span className="text-[10px] text-slate-400">
            {connected ? session.yourCarClassShortName ?? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      <div className="px-3 py-2 space-y-1.5" style={{ WebkitAppRegion: "no-drag" } as React.CSSProperties}>

        {/* Plan Updated flash */}
        {planUpdatedFlash && activePlan && (
          <div className="rounded border border-cyan-500/70 bg-cyan-900/50 px-2 py-1 text-center text-[10px] text-cyan-300">
            ↺ PLAN UPDATED — {activePlan.pitStops} stops · {activePlan.adjustedFuelPerLap.toFixed(2)}L/lap · {fmtTime(activePlan.adjustedLapTime)}
          </div>
        )}

        {/* Action Banner */}
        <div className={`rounded-lg border-2 ${ac.border} ${ac.bg} px-3 py-2 text-center`}>
          <div className={`text-lg font-black tracking-wide ${ac.text}`}>
            {ac.icon} {ac.label}
          </div>
          <div className="text-[10px] text-slate-300 mt-0.5 leading-tight">
            {!connected
              ? 'Waiting for iRacing...'
              : !hasPlan
              ? 'Select a plan on the Strategy tab'
              : overlayState.message}
          </div>
        </div>

        {hasPlan && hasData && connected && (
          <>
            {/* Targets Row */}
            <div className="grid grid-cols-2 gap-1.5">
              {/* Fuel Target */}
              <div className="bg-slate-800/70 rounded border border-slate-700/40 px-2 py-1.5">
                <div className="text-[9px] text-slate-500 uppercase">Fuel Target</div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold text-white">
                    {overlayState.targetFuelPerLap.toFixed(2)}
                    <span className="text-[10px] text-slate-400 ml-0.5">L/lap</span>
                  </span>
                  <span className={`text-[10px] font-bold ${overlayState.fuelDelta > 0.05 ? 'text-red-400' : overlayState.fuelDelta < -0.05 ? 'text-blue-400' : 'text-green-400'}`}>
                    {fmtDelta(overlayState.fuelDelta, 'L')}
                  </span>
                </div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  Avg: {overlayState.actualFuelPerLap.toFixed(2)} L/lap
                  {lastLapFuel !== null && (
                    <span className="ml-1.5 text-slate-400">Last: {lastLapFuel.toFixed(2)}L</span>
                  )}
                </div>
              </div>

              {/* Pace Target */}
              <div className="bg-slate-800/70 rounded border border-slate-700/40 px-2 py-1.5">
                <div className="text-[9px] text-slate-500 uppercase">Pace Target</div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold text-white">
                    {fmtTime(overlayState.targetLapTime)}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {fmtDelta(overlayState.paceDelta, 's')}
                  </span>
                </div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  Now: {fmtTime(overlayState.actualLapTime)}
                </div>
              </div>
            </div>

            {/* Stint & Pit Info */}
            <div className="grid grid-cols-4 gap-1.5">
              <MiniBox label="Fuel" value={`${overlayState.fuelRemaining.toFixed(1)}L`}
                warn={overlayState.fuelLapsLeft <= 3} />
              <MiniBox label="Fuel Laps" value={`${overlayState.fuelLapsLeft}`}
                warn={overlayState.fuelLapsLeft <= 3} />
              <MiniBox label="To Pit" value={overlayState.pitsRemaining > 0 ? `${overlayState.lapsUntilPit}` : "—"}
                warn={overlayState.lapsUntilPit <= 2 && overlayState.pitsRemaining > 0} />
              <MiniBox label="Stops" value={`${overlayState.pitsRemaining}`} />
            </div>

            {/* Next Pit Details */}
            {overlayState.pitsRemaining > 0 && overlayState.nextPitType && (
              <div className="text-center text-[10px] text-slate-400 bg-slate-800/40 rounded px-2 py-1 border border-slate-700/30">
                Next pit: <span className="text-white font-bold">{overlayState.nextPitType === 'full' ? 'Full' : 'Splash'}</span>
                {overlayState.nextPitFuelAdd > 0 && (
                  <> — add <span className="text-white font-bold">{overlayState.nextPitFuelAdd.toFixed(1)}L</span></>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-[9px] text-slate-500">
              Lap {laps.length} · {overlayState.lapsRemaining} laps left
              {remainStr ? ` · ${remainStr}` : ''}
              {status.trackName ? ` · ${status.trackName}` : ''}
            </div>
          </>
        )}

        {/* No plan — show basic telemetry */}
        {!hasPlan && connected && hasData && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-3 gap-1.5">
              <MiniBox label="Lap" value={`${laps.length}`} />
              <MiniBox label="Fuel" value={`${currentFuel?.toFixed(1) ?? '--'}L`} />
              <MiniBox label="Pace" value={fmtTime(recentAvg.lapTime)} />
            </div>
            <div className="text-center text-[9px] text-slate-500">
              Select a plan on the Strategy tab for targets
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Mini Box ─────────────────────────────────────────────────────────────

const MiniBox: React.FC<{ label: string; value: string; warn?: boolean }> = ({ label, value, warn }) => (
  <div className={`rounded px-1.5 py-1 border text-center ${
    warn ? "bg-red-900/40 border-red-500/50" : "bg-slate-800/60 border-slate-700/40"
  }`}>
    <div className="text-[8px] text-slate-500 uppercase">{label}</div>
    <div className={`text-[11px] font-bold ${warn ? "text-red-400" : "text-white"}`}>{value}</div>
  </div>
);
